import { Coffee, MessageSquare, Mail, Plus } from 'lucide-react'
import { track } from '../lib/analytics'

/**
 * The four actions, as a fixed bar across the bottom of every page.
 *
 * SPEC: Lisa, recorded 2026-09-07. Verbatim on the parts that decide the code:
 * "four icons featured on the bottom of every page, whether it's mobile or
 * nonmobile", left to right envelope/Subscribe, speech bubble/Feedback, plus
 * sign/Add event, "a steaming coffee cup" with Donate under it. On the styling:
 * "the button should be the color with the off white text color, and the graphic
 * should be simplistic line work."
 *
 * So: brick ground, paper text, Lucide outlines at a thin stroke. Off-white on
 * brick-600 measures 8.69:1, well past AA, which matters at a 10px label.
 *
 * "Donate" over "Support" or "Ko-Fi" — the recording talks itself through all
 * three and lands on Donate. It is also the right answer next to a Feedback tab,
 * where "Support" collects the taps meant for the thing beside it.
 *
 * WHY THESE FOUR TOGETHER. They were scattered: Subscribe was a brick button
 * competing with four nav links in a 56px bar, Add event sat beside the
 * week/month toggle, Feedback was a floating circle over the list, and Ko-fi was
 * a sentence in the footer a phone reader had to scroll the whole calendar to
 * reach. None is a NAVIGATION target - they are things you DO - so they sit
 * together at thumb height instead of spread through the chrome.
 */
export function BottomTabs({
  onSubscribe,
  onFeedback,
  onAddEvent,
}: {
  onSubscribe: () => void
  onFeedback: () => void
  onAddEvent: () => void
}) {
  // "simplistic line work": Lucide outlines, and strokeWidth 1.75 rather than the
  // default 2 so they read as line art rather than as a bold glyph. h-5 rather
  // than the house h-2 default, which is the measure for an icon sitting INSIDE a
  // line of text; at 8px above a label it stops being identifiable.
  const icon = 'h-5 w-5'
  const tab =
    'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-paper transition-colors hover:bg-brick-700 sm:text-xs'

  return (
    <nav
      // Phones only (Andrew, 2026-09-07, overriding the recording's "whether it's
      // mobile or nonmobile"). Desktop has the room to keep these where they are:
      // the subscribe CTA in the top bar, Submit event by the view toggle, and
      // the feedback button floating bottom-right.
      // pb for the iOS home indicator, or the labels sit under it on any recent
      // iPhone.
      className="fixed inset-x-0 bottom-0 z-30 flex bg-brick-600 pb-[env(safe-area-inset-bottom)] md:hidden"
      aria-label="Actions"
    >
      <button
        type="button"
        onClick={() => {
          track('subscribe_modal_opened', { from: 'bottom_tabs' })
          onSubscribe()
        }}
        className={tab}
      >
        <Mail className={icon} strokeWidth={1.75} />
        Subscribe
      </button>

      <button
        type="button"
        onClick={() => {
          track('feedback_opened', { from: 'bottom_tabs' })
          onFeedback()
        }}
        className={tab}
      >
        <MessageSquare className={icon} strokeWidth={1.75} />
        Feedback
      </button>

      <button
        type="button"
        onClick={() => {
          track('submit_event_opened', { from: 'bottom_tabs' })
          onAddEvent()
        }}
        className={tab}
      >
        {/* A bare plus, per the spec, not a calendar-with-a-plus: the label
            already says what is being added. */}
        <Plus className={icon} strokeWidth={1.75} />
        Add event
      </button>

      {/* A link, not a button: it leaves the site, so long-press "open in new
          tab" should work on it like any other link. */}
      <a
        href="https://ko-fi.com/lyhevents"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track('kofi_clicked', { from: 'bottom_tabs' })}
        className={tab}
      >
        <Coffee className={icon} strokeWidth={1.75} />
        Donate
      </a>
    </nav>
  )
}
